/**
 * Vraie page d'accès (site verrouillé).
 * Parle au backend pour valider le code et obtenir un token.
 * Le token est stocké en localStorage avec sa date d'expiration.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Lock, MessageCircle, Mail, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = 'https://wa.me/message/XTYPH2HUY3L6L1';
const EMAIL_ADDRESS = 'rhoneamitie@gmail.com';
const BG_URL = 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1920&q=80';
const TOKEN_KEY = 'raav_access_token';

export const saveToken = (token, expiresAt) => {
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify({ token, expires_at: expiresAt }));
  } catch { /* ignore */ }
};

export const clearToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
};

export const getStoredToken = () => {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.token) return null;
    if (obj.expires_at && new Date(obj.expires_at) < new Date()) return null;
    return obj.token;
  } catch { return null; }
};

const AccessGate = ({ onUnlock }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) { setError('Merci de saisir un code.'); return; }
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/access/verify`, { code: code.trim() });
      if (r.data?.success && r.data?.token) {
        saveToken(r.data.token, r.data.expires_at);
        if (onUnlock) onUnlock();
        else window.location.reload();
      } else {
        setError('Code incorrect.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Code incorrect.';
      setError(`${detail} Si vous n'êtes pas sûr(e), contactez-nous sur WhatsApp, nous vous redonnerons votre code.`);
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden flex flex-col items-center justify-center px-4 py-10" data-testid="access-gate">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG_URL})` }} aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1E3A5F]/70 via-[#1E3A5F]/55 to-[#0b1d33]/85" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-lg mx-auto">
        <div className="flex justify-center mb-6">
          <div className="w-28 h-28 rounded-full bg-white/15 backdrop-blur-md border-2 border-white/30 flex items-center justify-center shadow-2xl">
            <div className="text-center text-white leading-tight">
              <div className="text-[10px] tracking-[0.2em] font-semibold opacity-80">ASSOCIATION</div>
              <div className="text-[14px] font-bold">RHÔNE ALPES</div>
              <div className="text-[14px] font-bold">AMITIÉ</div>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-7 sm:p-9 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-white text-2xl sm:text-3xl font-bold leading-tight">
              Bienvenue sur le site de<br />
              <span className="text-[#5BC5D4]">Rhône Alpes Amitié Voyages</span>
            </h1>
            <p className="text-white/80 text-sm italic">« On ne se prend pas au sérieux, mais on est sérieux. »</p>
          </div>

          <div className="bg-white/10 border border-white/20 rounded-2xl p-4 text-white/95 text-sm leading-relaxed text-center">
            <p className="font-semibold mb-2">Ce site est réservé à nos adhérents.</p>
            <p className="text-white/85">Vous y retrouverez toutes les informations sur nos voyages, vous pourrez vous inscrire et effectuer vos règlements en toute simplicité.</p>
          </div>

          <div className="space-y-3">
            <div className="text-center">
              <p className="text-white/90 text-sm leading-relaxed">Vous souhaitez <strong>en savoir plus</strong> sur notre association et sur nos voyages ?</p>
              <p className="text-white/80 text-xs mt-1">Contactez-nous, nous serons ravis de vous accueillir !</p>
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
              className="group flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#1DA851] text-white font-bold py-4 rounded-2xl shadow-lg transition-all hover:shadow-2xl hover:scale-[1.02]"
              data-testid="gate-whatsapp">
              <MessageCircle className="w-5 h-5" />
              Nous contacter sur WhatsApp
              <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </a>
            <a href={`mailto:${EMAIL_ADDRESS}?subject=Demande%20d%27acc%C3%A8s%20au%20site&body=Bonjour%2C%0A%0AJe%20d%C3%A9couvre%20votre%20association%20et%20je%20souhaiterais%20recevoir%20un%20code%20d%27acc%C3%A8s%20au%20site.%0A%0AMerci%20%21`}
              className="group flex items-center justify-center gap-3 w-full bg-white/20 hover:bg-white/30 border border-white/30 text-white font-semibold py-3 rounded-2xl transition-all hover:shadow-lg"
              data-testid="gate-email">
              <Mail className="w-4 h-4" />
              Ou nous écrire par email
            </a>
          </div>

          <div className="flex items-center gap-3 text-white/50 text-xs uppercase tracking-widest">
            <div className="flex-grow h-px bg-white/20" />
            <span>ou</span>
            <div className="flex-grow h-px bg-white/20" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="flex items-center justify-center gap-2 text-white/90 text-sm font-medium">
              <Lock className="w-4 h-4" />
              Vous avez déjà un code d'accès ?
            </label>
            <input type="text" value={code} onChange={(e) => { setCode(e.target.value); setError(''); }}
              placeholder="Entrez votre code" autoComplete="off"
              className="w-full bg-white/90 text-[#1E3A5F] placeholder-gray-400 rounded-2xl px-5 py-3.5 text-center text-base font-semibold tracking-wide outline-none border-2 border-transparent focus:border-[#5BC5D4] transition"
              data-testid="gate-code-input" />
            {error && (
              <div className="bg-white/15 border border-amber-300/40 rounded-xl p-3 text-white/90 text-xs text-center leading-relaxed" data-testid="gate-error">{error}</div>
            )}
            <button type="submit" disabled={loading}
              className="w-full bg-[#5BC5D4] hover:bg-[#4AB3C2] text-white font-bold py-3.5 rounded-2xl shadow-lg transition-all hover:shadow-xl disabled:opacity-60"
              data-testid="gate-submit">
              {loading ? 'Vérification…' : 'Entrer'}
            </button>
          </form>
        </div>

        <p className="text-center text-white/60 text-xs mt-6">Association loi 1901 — Depuis 1997</p>
      </div>
    </div>
  );
};

export default AccessGate;
